import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, CircularProgress, Container } from '@material-ui/core';
import UplotReact from 'uplot-react';
import 'uplot/dist/uPlot.min.css';
import { HrvTimeSeriesData, HrvChartChunk } from '../../../types/hrvTimeSeries';

interface HrvTimeSeriesChartProps {
  data: HrvTimeSeriesData | null;
  startTime: number; // Timestamp in milliseconds
  loading?: boolean;
  error?: Error | null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartContainer: {
    marginBottom: theme.spacing(4),
  },
}));

/**
 * HRV Time-Series Chart Component
 *
 * Displays HRV metrics (SDNN, RMSSD, pNN50) and mean heart rate
 * as time-series line charts. X-axis shows time in HH:mm:ss format
 * relative to recording start time. Automatically chunks data into
 * 6-hour segments for optimal visualization of long recordings.
 *
 * @param data - HRV time-series data from GraphQL API
 * @param startTime - Recording start time in milliseconds for X-axis time formatting
 * @param loading - Whether data is currently loading
 * @param error - Error object if data fetch failed
 */
export const HrvTimeSeriesChart: React.FC<HrvTimeSeriesChartProps> = ({
  data,
  startTime,
  loading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();

  // T022: Implement chunking function
  const chunkHrvDataInSixHours = useMemo(
    () =>
      (hrvData: HrvTimeSeriesData): HrvChartChunk[] => {
        const SIX_HOURS_IN_SECONDS = 6 * 60 * 60;
        const chunks: HrvChartChunk[] = [];

        if (!hrvData.timestamps.length) {
          return chunks;
        }

        let chunkStart = hrvData.timestamps[0];
        let tempData: HrvChartChunk = {
          timestamps: [],
          sdnn: [],
          rmssd: [],
          pnn50: [],
          meanHrBpm: [],
        };

        for (let i = 0; i < hrvData.timestamps.length; i += 1) {
          if (hrvData.timestamps[i] - chunkStart < SIX_HOURS_IN_SECONDS) {
            tempData.timestamps.push(hrvData.timestamps[i]);
            tempData.sdnn.push(hrvData.sdnn[i]);
            tempData.rmssd.push(hrvData.rmssd[i]);
            tempData.pnn50.push(hrvData.pnn50[i]);
            tempData.meanHrBpm.push(hrvData.meanHrBpm[i]);
          } else {
            chunks.push(tempData);
            chunkStart = hrvData.timestamps[i];
            tempData = {
              timestamps: [hrvData.timestamps[i]],
              sdnn: [hrvData.sdnn[i]],
              rmssd: [hrvData.rmssd[i]],
              pnn50: [hrvData.pnn50[i]],
              meanHrBpm: [hrvData.meanHrBpm[i]],
            };
          }
        }

        if (tempData.timestamps.length > 0) {
          chunks.push(tempData);
        }

        return chunks;
      },
    [],
  );

  // T023: Loading state
  if (loading) {
    return (
      <Container className={classes.root} component={Paper} data-testid="loading-spinner">
        <Typography variant="h6" gutterBottom>
          {t('hrvChart.title')}
        </Typography>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </div>
      </Container>
    );
  }

  // T024: Error state
  if (error || !data) {
    return (
      <Paper style={{ padding: 16 }}>
        <Typography color="error">{t('hrvChart.errorMessage')}</Typography>
      </Paper>
    );
  }

  // T025: Empty data handling
  if (!data.timestamps.length || data.timestamps.length === 0) {
    return null; // Hide chart section
  }

  // Check if all values are null
  const hasValidData =
    data.sdnn.some((v) => v !== null) ||
    data.rmssd.some((v) => v !== null) ||
    data.pnn50.some((v) => v !== null);

  if (!hasValidData) {
    return (
      <Paper style={{ padding: 16 }}>
        <Typography color="textSecondary">{t('hrvChart.noDataMessage')}</Typography>
      </Paper>
    );
  }

  const chunks = chunkHrvDataInSixHours(data);

  // T026: Configure UPlot options with dual Y-axes and time-based X-axis
  const createChartOptions = () => ({
    width: 754,
    height: 300,
    title: t('hrvChart.title'),
    scales: {
      x: {
        time: true, // Enable time-based formatting (like RateChart)
      },
      hrv: {
        auto: true,
      },
      hr: {
        auto: true,
      },
    },
    axes: [
      {
        label: t('hrvChart.axisTimeLabel'),
      },
      {
        scale: 'hrv',
        side: 3, // left
        label: t('hrvChart.axisHrvLabel'),
        stroke: theme.palette.primary.main,
      },
      {
        scale: 'hr',
        side: 1, // right
        label: t('hrvChart.axisHrLabel'),
        stroke: theme.palette.warning.main,
      },
    ],
    series: [
      {
        label: t('common.seriesTimeLabel'),
      },
      {
        label: t('hrvChart.seriesSdnn'),
        scale: 'hrv',
        stroke: theme.palette.primary.main,
        width: 1,
      },
      {
        label: t('hrvChart.seriesRmssd'),
        scale: 'hrv',
        stroke: theme.palette.secondary.main,
        width: 1,
      },
      {
        label: t('hrvChart.seriesPnn50'),
        scale: 'hrv',
        stroke: theme.palette.error.main,
        width: 1,
      },
      {
        label: t('hrvChart.seriesMeanHr'),
        scale: 'hr',
        stroke: theme.palette.warning.main,
        width: 1,
      },
    ],
  });

  // T027: Transform data to UPlot format
  // Convert timestamp offsets (seconds) to Unix timestamps (seconds)
  const toUPlotData = (chunk: HrvChartChunk): any => [
    chunk.timestamps.map((offset) => (startTime / 1000) + offset), // Convert to Unix time
    chunk.sdnn,
    chunk.rmssd,
    chunk.pnn50,
    chunk.meanHrBpm,
  ];

  // T028: Render chart with UPlot
  return (
    <Container className={classes.root} component={Paper}>
      {chunks.map((chunk) => (
        <div
          className={classes.chartContainer}
          key={`hrv-chunk-${chunk.timestamps[0]}-${chunk.timestamps[chunk.timestamps.length - 1]}`}
          data-testid="hrv-chart-chunk"
        >
          <UplotReact options={createChartOptions()} data={toUPlotData(chunk)} />
        </div>
      ))}
    </Container>
  );
};
